import {
  buildIncome,
  buildFixed,
  buildFlexible,
  buildDebts,
} from "../domain/entities/mapping";
import type { FinancialStatementResponseDto } from "../api/dto/FinancialStatement";
import type { KeyedOption } from "../types/common";

export type StepId = "about" | "income" | "fixed" | "flexible" | "debts";

export const incomeMeta = [
  { key: "customerSalaryWage", title: "Salary/Wage", category: "Income" },
  { key: "otherEarnings", title: "Other earnings", category: "Income" },
  {
    key: "partnerSalaryWage",
    title: "Partner salary/wage",
    category: "Income",
  },
  // Benefit
  { key: "carersAllowance", title: "Carers allowance", category: "Benefits" },
  { key: "childBenefit", title: "Child benefit", category: "Benefits" },
  { key: "childTaxCredit", title: "Child tax credit", category: "Benefits" },
  { key: "childrenUnder16", title: "Children under 16", category: "Benefits" },
  {
    key: "councilTaxSupport",
    title: "Council tax support",
    category: "Benefits",
  },
  {
    key: "disabilityBenefits",
    title: "Disability benefits",
    category: "Benefits",
  },
  {
    key: "emplymentSupportAllowanceorSSP",
    title: "Employment & support allowance / SSP",
    category: "Benefits",
  },
  { key: "incomeSupport", title: "Income support", category: "Benefits" },
  {
    key: "jobseekersAllowanceContributionBased",
    title: "Jobseekers allowance (contribution based)",
    category: "Benefits",
  },
  {
    key: "jobseekersAllowanceIncomeBased",
    title: "Jobseekers allowance (income based)",
    category: "Benefits",
  },
  {
    key: "localHousingAllowanceHousingBenefit",
    title: "Local housing allowance / Housing benefits",
    category: "Benefits",
  },
  {
    key: "otherBenefitsTaxCredits",
    title: "Other benefits tax credits",
    category: "Benefits",
  },
  { key: "universalCredit", title: "Universal credit", category: "Benefits" },
  {
    key: "workingTaxCredit",
    title: "Working tax credit",
    category: "Benefits",
  },
  // Pensions
  { key: "otherPensions", title: "Other pensions", category: "Pensions" },
  { key: "pensionCredit", title: "Pensions credit", category: "Pensions" },
  {
    key: "privateorWorkPensions",
    title: "Private or work pensions",
    category: "Pensions",
  },
  { key: "statePensions", title: "State pensions", category: "Pensions" },
  // Other
  { key: "boardersorLodgers", title: "Boarders or lodgers", category: "Other" },
  {
    key: "maintenanceorChildSupport",
    title: "Maintenance or child support",
    category: "Other",
  },
  {
    key: "nonDependentsContributions",
    title: "Non-dependents contributions",
    category: "Other",
  },
  { key: "otherIncome", title: "Other income", category: "Other" },
  {
    key: "studentLoansandGrants",
    title: "Student loans & grants",
    category: "Other",
  },
];

export const fixedMeta = [
  {
    key: "applianceFurnitureRental",
    title: "Appliance & furniture rental",
    category: "Home & Contents",
  },
  {
    key: "councilTaxRates",
    title: "Council tax/rates",
    category: "Home & Contents",
  },
  {
    key: "groundRentServiceCharges",
    title: "Ground rent & service charges",
    category: "Home & Contents",
  },
  { key: "homeOtherCosts", title: "Other costs", category: "Home & Contents" },
  { key: "mortgage", title: "Mortgage", category: "Home & Contents" },
  {
    key: "mortgageEndowment",
    title: "Mortgage endowment",
    category: "Home & Contents",
  },
  { key: "rent", title: "Rent", category: "Home & Contents" },
  { key: "securedLoans", title: "Secured loans", category: "Home & Contents" },
  { key: "tvLicence", title: "TV licence", category: "Home & Contents" },
  {
    key: "adultCareCosts",
    title: "Adult-care costs",
    category: "Care & Health",
  },
  { key: "careOtherCosts", title: "Other costs", category: "Care & Health" },
  {
    key: "childMaintenanceorChildSupport",
    title: "Child maintenance",
    category: "Care & Health",
  },
  {
    key: "childcareCosts",
    title: "Childcare costs",
    category: "Care & Health",
  },
  {
    key: "dentistryandOpticians",
    title: "Dentistry & opticians",
    category: "Care & Health",
  },
  {
    key: "prescriptionsandMedicines",
    title: "Prescriptions & medicine",
    category: "Care & Health",
  },
  {
    key: "buildingsandContent",
    title: "Buildings & content",
    category: "Pensions & Insurance",
  },
  {
    key: "healthInsurance",
    title: "Health insurance",
    category: "Pensions & Insurance",
  },
  {
    key: "lifeInsurance",
    title: "Life insurance",
    category: "Pensions & Insurance",
  },
  {
    key: "mortgagePaymentProtection",
    title: "Mortgage payment protection",
    category: "Pensions & Insurance",
  },
  {
    key: "pensionOtherCosts",
    title: "Other costs",
    category: "Pensions & Insurance",
  },
  {
    key: "pensionPayments",
    title: "Pension payments",
    category: "Pensions & Insurance",
  },
  { key: "electricity", title: "Electricity", category: "Utilities" },
  { key: "gas", title: "Gas", category: "Utilities" },
  {
    key: "utilOtherCosts",
    title: "Other costs (coal, oil etc.)",
    category: "Utilities",
  },
  {
    key: "utilOtherExpenditure",
    title: "Other expenditure",
    category: "Utilities",
  },
  { key: "waterSupply", title: "Water supply", category: "Water" },
  { key: "waterWaste", title: "Water waste", category: "Water" },
  {
    key: "afterSchoolClubsandSchoolTrips",
    title: "After-school clubs & school trips",
    category: "School Costs",
  },
  { key: "schoolOtherCosts", title: "Other costs", category: "School Costs" },
  { key: "schoolUniform", title: "School uniform", category: "School Costs" },
  {
    key: "prefessionalFees",
    title: "Professional fees",
    category: "Professional Costs",
  },
  {
    key: "professionalCourses",
    title: "Professional courses",
    category: "Professional Costs",
  },
  {
    key: "profressionalOtherCosts",
    title: "Other costs",
    category: "Professional Costs",
  },
  { key: "unionFees", title: "Union fees", category: "Professional Costs" },
  {
    key: "breakdownCover",
    title: "Breakdown cover",
    category: "Transport & Travel",
  },
  {
    key: "carInsurance",
    title: "Car insurance",
    category: "Transport & Travel",
  },
  {
    key: "fuelParkingTollCharges",
    title: "Fuel, parking, toll charges",
    category: "Transport & Travel",
  },
  {
    key: "hirePurchase",
    title: "Hire purchase",
    category: "Transport & Travel",
  },
  {
    key: "moTandongoingMaintenance",
    title: "MOT & ongoing maintenance",
    category: "Transport & Travel",
  },
  {
    key: "publicTransport",
    title: "Public transport",
    category: "Transport & Travel",
  },
  { key: "roadTax", title: "Road tax", category: "Transport & Travel" },
  {
    key: "transportOtherCosts",
    title: "Other costs (inc taxis)",
    category: "Transport & Travel",
  },
];

export const flexibleMeta = [
  {
    key: "communicationsOtherCosts",
    title: "Other costs",
    category: "Communications & Leisure",
  },
  {
    key: "gifts",
    title: "Gifts (e.g. birthdays)",
    category: "Communications & Leisure",
  },
  {
    key: "hobbiesLeisureOrSport",
    title: "Hobbies, leisure or sport",
    category: "Communications & Leisure",
  },
  {
    key: "homePhoneInternetTVPackage",
    title: "Home phone",
    category: "Communications & Leisure",
  },
  {
    key: "mobilePhone",
    title: "Mobile phone",
    category: "Communications & Leisure",
  },
  {
    key: "newspapersMagazinesStationaryPostage",
    title: "Newspapers, magazines etc.",
    category: "Communications & Leisure",
  },
  {
    key: "pocketMoney",
    title: "Pocket money",
    category: "Communications & Leisure",
  },
  { key: "alcohol", title: "Alcohol", category: "Food & Housekeeping" },
  { key: "groceries", title: "Groceries", category: "Food & Housekeeping" },
  {
    key: "houseRepairsAndMaintenance",
    title: "House repairs & maintenance",
    category: "Food & Housekeeping",
  },
  {
    key: "housekeepingOtherCosts",
    title: "Other costs",
    category: "Food & Housekeeping",
  },
  {
    key: "laundryandDryCleaning",
    title: "Laundry & dry cleaning",
    category: "Food & Housekeeping",
  },
  {
    key: "nappiesAndBabyItems",
    title: "Nappies & baby items",
    category: "Food & Housekeeping",
  },
  {
    key: "schoolMealsAndMealsAtWork",
    title: "School meals & work meals",
    category: "Food & Housekeeping",
  },
  {
    key: "smokingProducts",
    title: "Smoking products",
    category: "Food & Housekeeping",
  },
  {
    key: "vetBillsandPetInsurance",
    title: "Vet bills",
    category: "Food & Housekeeping",
  },
  {
    key: "clothingandFootwear",
    title: "Clothing & footwear",
    category: "Personal Costs",
  },
  { key: "hairdressing", title: "Hairdressing", category: "Personal Costs" },
  {
    key: "personalOtherCosts",
    title: "Other costs",
    category: "Personal Costs",
  },
  { key: "toiletries", title: "Toiletries", category: "Personal Costs" },
];

export const debtsMeta = [
  {
    key: "councilTaxArrears",
    title: "Council tax",
    category: "Priority Debts",
    isDebt: true,
  },
  {
    key: "hirePurchase",
    title: "Hire purchase",
    category: "Priority Debts",
    isDebt: true,
  },
  {
    key: "maintenance",
    title: "Maintenance",
    category: "Priority Debts",
    isDebt: true,
  },
  {
    key: "migistrateFines",
    title: "Magistrate fines",
    category: "Priority Debts",
    isDebt: true,
  },
  {
    key: "mortgageArrears",
    title: "Mortgage arrears",
    category: "Priority Debts",
    isDebt: true,
  },
  {
    key: "otherArrears",
    title: "Other arrears",
    category: "Priority Debts",
    isDebt: true,
  },
  {
    key: "otherSecuredLoans",
    title: "Other secured loans",
    category: "Priority Debts",
    isDebt: true,
  },
  {
    key: "rentArrears",
    title: "Rent arrears",
    category: "Priority Debts",
    isDebt: true,
  },
  {
    key: "utilitiesArrears",
    title: "Utilities arrears",
    category: "Priority Debts",
    isDebt: true,
  },
  {
    key: "creditCardArrears",
    title: "Credit card arrears",
    category: "Non Priority Debts",
    isDebt: true,
  },
  {
    key: "otherDebtArrears",
    title: "Other debt arrears",
    category: "Non Priority Debts",
    isDebt: true,
  },
  {
    key: "overdrafts",
    title: "Overdrafts",
    category: "Non Priority Debts",
    isDebt: true,
  },
  {
    key: "storeCardArrears",
    title: "Store card arrears",
    category: "Non Priority Debts",
    isDebt: true,
  },
  {
    key: "unsecuredLoansArrears",
    title: "Unsecured loan arrears",
    category: "Non Priority Debts",
    isDebt: true,
  },
];

export const categoryByKey = (key: string, parent?: string) => {
  if (parent === "Additional Debt") {
    return debtsMeta.find((m) => m.key === key)?.category ?? "";
  }
  return (
    incomeMeta.find((m) => m.key === key) ??
    fixedMeta.find((m) => m.key === key) ??
    flexibleMeta.find((m) => m.key === key) ??
    debtsMeta.find((m) => m.key === key)
  )?.category ?? "";
};

export const titleByKey = (key: string, parent?: string) => {
  if (parent === "Additional Debt") {
    return debtsMeta.find((m) => m.key === key)?.title ?? "";
  }
  return (
    incomeMeta.find((m) => m.key === key) ??
    fixedMeta.find((m) => m.key === key) ??
    flexibleMeta.find((m) => m.key === key) ??
    debtsMeta.find((m) => m.key === key)
  )?.title ?? "";
};

export const steps: KeyedOption<StepId, string>[] = [
  { id: "about", label: "About you" },
  { id: "income", label: "Income" },
  { id: "fixed", label: "Expenditure (Fixed)" },
  { id: "flexible", label: "Expenditure (Flexible)" },
  { id: "debts", label: "Additional Debt" },
];

// ---- Dynamic meta-generation from API response ----
export type FinancialStatementMetaEntry = {
  key: string;
  title: string;
  category: string;
  isDebt?: boolean;
};

export interface FinancialStatementMetaFromApi {
  income: FinancialStatementMetaEntry[];
  fixed: FinancialStatementMetaEntry[];
  flexible: FinancialStatementMetaEntry[];
  debts: FinancialStatementMetaEntry[];
}

export const buildMetaFromApi = (
  response: FinancialStatementResponseDto,
): FinancialStatementMetaFromApi => {
  const incomeItems = buildIncome(response?.income ?? {});
  const fixedItems = buildFixed(response?.fixed ?? {});
  const flexibleItems = buildFlexible(response?.flexible ?? {});
  const debtItems = buildDebts(response?.debts ?? {});

  const mapEntry = (i: {
    key: string;
    title: string;
    category: string;
  }): FinancialStatementMetaEntry => ({
    key: i.key,
    title: i.title,
    category: i.category,
  });

  return {
    income: incomeItems.map(mapEntry),
    fixed: fixedItems.map(mapEntry),
    flexible: flexibleItems.map(mapEntry),
    debts: debtItems.map((i) => ({ ...mapEntry(i), isDebt: true })),
  };
};
